import { useState, useEffect, useCallback } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";

// Axes d'analyse (miroir de ANALYSIS_FOCUS côté backend)
const FOCUS_OPTIONS = [
  { value: "general", label: "Vue d'ensemble" },
  { value: "sante", label: "Santé des cultures" },
  { value: "fertilisation", label: "Fertilisation & sol" },
  { value: "irrigation", label: "Irrigation & eau" },
  { value: "rendement", label: "Rendement & production" },
  { value: "sanitaire", label: "Risques sanitaires" },
];

export default function AnalyseIATab({ parcelId }) {
  const [analyses, setAnalyses] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState("general");
  const [question, setQuestion] = useState("");

  const fetchAnalyses = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/analyses`);
      if (res.ok) setAnalyses(await res.json());
    } catch (err) { console.error('[AnalyseIATab] fetch failed:', err); }
    setLoading(false);
  }, [parcelId]);

  useEffect(() => { fetchAnalyses(); }, [fetchAnalyses]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ focus, question: question.trim() }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAnalyses(prev => [{
        analysis_id: data.analysis_id,
        content: data.analysis,
        focus_label: data.focus_label,
        created_at: new Date().toISOString(),
      }, ...prev]);
      setQuestion("");
      toast.success("Analyse IA terminee");
    } catch {
      toast.error("Erreur lors de l'analyse IA");
    }
    setAnalyzing(false);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-black/5 space-y-2">
        <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: "#7A7A7A" }}>
          Axe de l'analyse
        </p>
        <div className="flex flex-wrap gap-1.5">
          {FOCUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              onClick={() => setFocus(o.value)}
              className={`text-[10.5px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${focus === o.value ? "text-white border-transparent" : "border-black/15 hover:bg-black/5"}`}
              style={focus === o.value ? { background: "#2D6A4F" } : { color: "#4A4A4A" }}
              data-testid={`ia-focus-${o.value}`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <input
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Question précise (optionnel) — ex. Quand replanter après la coupe ?"
          className="w-full h-8 text-[11.5px] border border-black/15 rounded-lg px-2.5"
          maxLength={500}
          data-testid="ia-question-input"
        />
        <Button size="sm" className="w-full h-9 text-[11px] font-semibold rounded-lg text-white gap-1.5"
          style={{ background: "#2D6A4F" }}
          onClick={handleAnalyze} disabled={analyzing} data-testid="ia-analyze-btn">
          {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {analyzing ? "Analyse en cours..." : "Lancer l'analyse"}
        </Button>
      </div>
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#2D6A4F" }} /></div>
        ) : analyses.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium" style={{ color: "#4A4A4A" }}>Aucune analyse disponible</p>
            <p className="text-xs mt-1" style={{ color: "#7A7A7A" }}>Lancez une analyse IA pour obtenir des recommandations agronomiques</p>
          </div>
        ) : (
          <div className="p-3 space-y-3">
            {analyses.map((a, i) => (
              <div key={a.analysis_id || i} className="p-3 rounded-xl border border-black/5" data-testid={`ia-analysis-${a.analysis_id || i}`}>
                <p className="text-[10px] uppercase tracking-wider mb-2 flex items-center gap-2 flex-wrap" style={{ color: '#7A7A7A' }}>
                  {a.created_at ? new Date(a.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : ''}
                  {a.focus_label && (
                    <span className="normal-case tracking-normal text-[9.5px] font-bold rounded-full px-2 py-0.5" style={{ background: "#dbe8f1", color: "#2f6a92" }}>
                      {a.focus_label}
                    </span>
                  )}
                </p>
                <div className="text-sm leading-relaxed" style={{ color: '#1A1A1A' }}>
                  {(a.content || '').split('\n').map((line, j) => (
                    <p key={`${a.analysis_id}-${j}`} className={line.startsWith('#') ? 'font-bold mt-2 text-xs' : 'mb-1 text-[12px]'}>
                      {line.replace(/^#+\s*/, '')}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
